import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { persistentStorage, STORAGE_KEYS } from '@/shared/utils/storage'

export type ApiEnv = 'AP' | 'DEV'

/**
 * 배포 대상별 AP/DEV API 주소를 빌드 시 주입한다.
 * 로그인 회사 코드와 웹뷰 env 파라미터는 실행 중 AP/DEV를 선택한다.
 */
const HOSTS = {
  AP: {
    baseUrl: import.meta.env.VITE_API_URL_AP,
    aiUrl: 'https://ai-assistant.bizbee.co.kr',
    groupwareUrl: 'https://works.bizbee.co.kr',
  },
  DEV: {
    baseUrl: import.meta.env.VITE_API_URL_DEV,
    aiUrl: 'https://dev-ai-assistant.bizbee.ne.kr',
    groupwareUrl: 'https://groupware.bizbee.ne.kr',
  },
} as const

/** 앱 웹뷰는 네이티브에서 로그인해서 serverCheck 를 안 거치므로 ?env= 로 넘겨받는다. */
function envFromUrl(): ApiEnv | null {
  if (typeof window === 'undefined') return null
  const env = new URLSearchParams(window.location.search).get('env')
  // 호스트가 지정한 운영 환경도 저장된 개발 환경보다 우선한다.
  return env === 'DEV' || env === 'AP' ? env : null
}

interface EnvState {
  env: ApiEnv
  baseUrl: string
  aiUrl: string
  groupwareUrl: string
  setEnv: (env: ApiEnv) => void
}

export const useEnvStore = create<EnvState>()(
  persist(
    (set) => ({
      env: envFromUrl() ?? 'AP',
      ...HOSTS[envFromUrl() ?? 'AP'],
      setEnv: (env) => set({ env, ...HOSTS[env] }),
    }),
    {
      name: STORAGE_KEYS.ENV,
      storage: persistentStorage,
      // 주소는 env 파생값 — env 만 저장하고 복원 시 다시 계산한다.
      // (예전처럼 baseUrl 을 저장하면 호스트를 바꿔도 옛 값이 스토리지에 굳는다)
      partialize: (state) => ({ env: state.env }) as EnvState,
      merge: (persisted, current) => {
        const env = envFromUrl() ?? (persisted as { env?: ApiEnv } | null)?.env ?? current.env
        return { ...current, env, ...HOSTS[env] }
      },
    },
  ),
)
